/**
 * Which settings differ from their defaults, and how to put them back.
 *
 * @module
 */
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";
import { ensureLocalApi, readLocalApi } from "../../localApi";
import { PROVIDER_SETTINGS } from "./providerSettingsCatalog";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import { Equal } from "effect";
import { useCallback, useMemo } from "react";

/** Report settings that differ from defaults and expose the shared restore action. */
export function useSettingsRestore(onRestored?: () => void) {
  const { setTheme } = useTheme();
  const settings = useSettings();
  const { resetSettings } = useUpdateSettings();

  const isGitWritingModelDirty = !Equal.equals(
    settings.textGenerationModelSelection ?? null,
    DEFAULT_UNIFIED_SETTINGS.textGenerationModelSelection ?? null,
  );
  const areProviderSettingsDirty = PROVIDER_SETTINGS.some((providerSettings) => {
    const currentSettings = settings.providers[providerSettings.provider];
    const defaultSettings = DEFAULT_UNIFIED_SETTINGS.providers[providerSettings.provider];
    return !Equal.equals(currentSettings, defaultSettings);
  });

  const changedSettingLabels = useMemo(
    // moved verbatim from the 2909-line ProviderSettings.tsx; complexity is
    // unchanged, and fallow reads a relocated function as a new one
    // fallow-ignore-next-line complexity
    () => [
      ...(settings.timestampFormat !== DEFAULT_UNIFIED_SETTINGS.timestampFormat
        ? ["Time format"]
        : []),
      ...(settings.theme !== DEFAULT_UNIFIED_SETTINGS.theme ? ["Theme"] : []),
      ...(settings.uiFontSize !== DEFAULT_UNIFIED_SETTINGS.uiFontSize ? ["UI font size"] : []),
      ...(settings.codeFontSize !== DEFAULT_UNIFIED_SETTINGS.codeFontSize
        ? ["Code font size"]
        : []),
      ...(settings.diffWordWrap !== DEFAULT_UNIFIED_SETTINGS.diffWordWrap
        ? ["Diff line wrapping"]
        : []),
      ...(settings.enableAssistantStreaming !== DEFAULT_UNIFIED_SETTINGS.enableAssistantStreaming
        ? ["Assistant output"]
        : []),
      ...(settings.coauthorIncludeForge !== DEFAULT_UNIFIED_SETTINGS.coauthorIncludeForge
        ? ["Forge co-author"]
        : []),
      ...(settings.defaultThreadEnvMode !== DEFAULT_UNIFIED_SETTINGS.defaultThreadEnvMode
        ? ["New thread mode"]
        : []),
      ...(settings.confirmThreadArchive !== DEFAULT_UNIFIED_SETTINGS.confirmThreadArchive
        ? ["Archive confirmation"]
        : []),
      ...(settings.confirmThreadDelete !== DEFAULT_UNIFIED_SETTINGS.confirmThreadDelete
        ? ["Delete confirmation"]
        : []),
      ...(settings.legacySidebarEnabled !== DEFAULT_UNIFIED_SETTINGS.legacySidebarEnabled
        ? ["Sidebar (legacy)"]
        : []),
      ...(isGitWritingModelDirty ? ["Git writing model"] : []),
      ...(areProviderSettingsDirty ? ["Providers"] : []),
      ...(!Equal.equals(settings.browser, DEFAULT_UNIFIED_SETTINGS.browser) ? ["Browser"] : []),
    ],
    [
      areProviderSettingsDirty,
      isGitWritingModelDirty,
      settings.browser,
      settings.codeFontSize,
      settings.coauthorIncludeForge,
      settings.confirmThreadArchive,
      settings.confirmThreadDelete,
      settings.defaultThreadEnvMode,
      settings.legacySidebarEnabled,
      settings.diffWordWrap,
      settings.enableAssistantStreaming,
      settings.timestampFormat,
      settings.theme,
      settings.uiFontSize,
    ],
  );

  const restoreDefaults = useCallback(async () => {
    if (changedSettingLabels.length === 0) return;
    const api = readLocalApi();
    const confirmed = await (api ?? ensureLocalApi()).dialogs.confirm(
      ["Restore default settings?", `This will reset: ${changedSettingLabels.join(", ")}.`].join(
        "\n",
      ),
    );
    if (!confirmed) return;

    setTheme(DEFAULT_UNIFIED_SETTINGS.theme);
    resetSettings();
    onRestored?.();
  }, [changedSettingLabels, onRestored, resetSettings, setTheme]);

  return {
    changedSettingLabels,
    restoreDefaults,
  };
}
